import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import type { DataTablePageEvent, DataTableRowClickEvent } from 'primereact/datatable'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import DataTable from '../../../components/ui/DataTable'
import Button from '../../../components/ui/Button'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import StatusTag from '../../../components/ui/StatusTag'
import Badge, { type BadgeTone } from '../../../components/ui/Badge'
import EmptyState from '../../../components/ui/EmptyState'
import { formatDate } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { PARTICIPATION_STATUS } from '../hooks/useUpdateParticipation'
import type { ActivityListItem, PriorityLevel } from '../types'

interface ActivityTableProps {
  items: ActivityListItem[]
  loading: boolean
  page: number
  rows: number
  totalElements: number
  onPage: (page: number, rows: number) => void
  onReview: (id: number) => void
  /** İşlemler menüsü: katılım durumu (GM kararı) güncelle (351 Katılıyor / 352 Katılmıyor). */
  onParticipation: (id: number, statusId: number) => void
}

/** Satır işlemleri — 3 nokta (kebab) popup menü: Katıl / Katılma. Kebab tıklaması satır tıklamasını yutmasın. */
function RowActions({
  row,
  onParticipation,
  t,
}: Readonly<{
  row: ActivityListItem
  onParticipation: (id: number, statusId: number) => void
  t: (k: string) => string
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    {
      label: t('activity.actions.attend'),
      icon: 'pi pi-check',
      command: () => onParticipation(row.id, PARTICIPATION_STATUS.ATTENDING),
    },
    {
      label: t('activity.actions.decline'),
      icon: 'pi pi-times',
      command: () => onParticipation(row.id, PARTICIPATION_STATUS.NOT_ATTENDING),
    },
  ]
  return (
    <span className="tk-acts">
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        aria-label={t('activity.actions.menu')}
        onClick={(e) => {
          e.stopPropagation()
          menuRef.current?.toggle(e)
        }}
      />
    </span>
  )
}

const PRIORITY_TONE: Record<PriorityLevel, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'blue',
  HIGH: 'green',
}

/**
 * Etkinlik Listesi tablosu — paylaşılan styled DataTable + ui bileşenleri (Talep Listesi kalıbı). Kolonlar:
 * Talep No (id) · Etkinlik · Yer/Tarih · Etkinlik Türü · Davet Türü · Stratejik Skor · Öncelik · Katılım Durumu. Satıra tıklama
 * detay/özete gider (onReview). Sunucu-taraflı sayfalama (lazy). Skor/öncelik yoksa hücre boş (anayasa).
 */
export default function ActivityTable({
  items,
  loading,
  page,
  rows,
  totalElements,
  onPage,
  onReview,
  onParticipation,
}: Readonly<ActivityTableProps>) {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()

  const placeDateBody = (row: ActivityListItem) => {
    const place = [row.city, row.country].filter(Boolean).join(' · ')
    const range = [row.startDate, row.endDate].filter(Boolean).map(formatDate).join(' – ')
    return (
      <div>
        <div>{place}</div>
        <div className="ui-cell-muted">{range}</div>
      </div>
    )
  }

  const scoreBody = (row: ActivityListItem) =>
    row.strategicScore == null ? '' : String(row.strategicScore)

  const priorityBody = (row: ActivityListItem) =>
    row.priority ? (
      <Badge tone={PRIORITY_TONE[row.priority.level]} variant="soft">
        {row.priority.name}
      </Badge>
    ) : (
      ''
    )

  const statusBody = (row: ActivityListItem) => (
    <StatusTag statusId={row.status?.id} label={row.status?.name} />
  )

  // Telefon/tablet genişliğinde (≤1024px, bkz. CARD_VIEW_BREAKPOINT) 8 kolonlu tablo yerine kart listesi
  // — aynı veri/sayfalama/tıklama/işlemler, yalnız sunum dalı değişir (bkz. ui.css `.ui-card-list`).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  /**
   * Boş durum açıklaması (*"Filtreleri değiştirin veya **yeni bir etkinlik girin**"*) bir EYLEM
   * ÇAĞRISI içeriyor; "Yeni Etkinlik" düğmesi artık `ACTIVITY_CREATE` ile kapılı olduğu için o
   * yetkisi olmayanda — Kısıtlı Yetkili asistan dahil — olmayan bir düğmeyi tarif ederdi. Aynı
   * kalıp Notlarım ve Ajanda'da da düzeltildi (push kapısı review'ü 21.10.2026). Başlık ("Etkinlik
   * bulunamadı") kalır: bilgiyi o taşıyor.
   */
  const emptyDesc = hasGrant(GRANTS.ACTIVITY_CREATE)
    ? t('activity.list.empty.desc')
    : undefined
  const showStatus = hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_VIEW)
  const showActions = hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_UPDATE)

  if (isMobile) {
    return (
      <div className="ui-card-list">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
          ))}
        {!loading && items.length === 0 && (
          <EmptyState
            title={t('activity.list.empty.title')}
            description={emptyDesc}
            icon="pi pi-calendar"
          />
        )}
        {!loading &&
          items.map((row) => {
            const place = [row.city, row.country].filter(Boolean).join(' · ')
            const range = [row.startDate, row.endDate].filter(Boolean).map(formatDate).join(' – ')
            return (
              // 15.09.2026 — **overlay-link**: kart kökü handler'sız, detayı başlıktaki gerçek
              // `<button>` açıyor ve kartı `::after` ile kaplıyor (`ui.css` → `.ui-card--linked`).
              // Kebab menüsü o kontrolün KARDEŞİdir. Önceki biçimde kart `role="button"` taşıyor ve
              // menü onun İÇİNDE kalıyordu; ARIA'da interaktif kontrol başka bir interaktif kontrol
              // içeremez (THY inceleme bulgusu, 15.09.2026; anayasa → kart kuralı). `isCardBodyClick`
              // / `e.target === e.currentTarget` elemesi yalnız DAVRANIŞI düzeltiyordu, semantiği
              // değil — kontroller kardeş olunca elemeye gerek kalmadı.
              <div key={row.id} className="ui-card ui-card--linked">
                <div className="ui-card-top">
                  <div className="ui-card-row" style={{ gap: '0.5rem', alignItems: 'center' }}  >
                  <span>{showStatus && <StatusTag statusId={row.status?.id} label={row.status?.name} />}</span>
                  {row.priority && (
                    <Badge tone={PRIORITY_TONE[row.priority.level]} variant="soft">
                      {row.priority.name}
                    </Badge>
                  )}
                  </div>
                  {showActions && !row.isProcessed && (
                    <span className="ui-card-actions ui-card-actions--compact">
                      <RowActions row={row} onParticipation={onParticipation} t={t} />
                    </span>
                  )}
                </div>
                <div className="ui-card-head">
                  <button type="button" className="ui-card-link" onClick={() => onReview(row.id)}>
                    <span className="ui-card-title">{row.name}</span>
                  </button>
                </div>
                <span className="ui-card-sub">
                  {[place, range].filter(Boolean).join(' · ')}
                </span>
                
                <div className="ui-card-row">
                  {row.eventType && <span>{row.eventType}</span>}
                  {row.invitationType && <span>{row.invitationType}</span>}
                  {row.strategicScore != null && <span>{row.strategicScore}</span>}
                </div>
              </div>
            )
          })}
        {!loading && totalElements > rows && (
          <Paginator
            first={page * rows}
            rows={rows}
            totalRecords={totalElements}
            onPageChange={(e: PaginatorPageChangeEvent) => onPage(e.page, e.rows)}
            template="PrevPageLink CurrentPageReport NextPageLink"
            className="ui-cards-paginator"
          />
        )}
      </div>
    )
  }

  return (
    <DataTable
      value={items}
      loading={loading}
      onRowClick={(e: DataTableRowClickEvent) => {
        // İşlemler (kebab) hücresinden gelen tıklama detaya gitmesin.
        const target = e.originalEvent?.target
        if (target instanceof Element && target.closest('.tk-acts')) return
        onReview((e.data as ActivityListItem).id)
      }}
      rowClassName={() => 'ui-row-clickable'}
      lazy
      paginator={totalElements > 0}
      rows={rows}
      first={page * rows}
      totalRecords={totalElements}
      onPage={(e: DataTablePageEvent) => onPage(e.page ?? 0, e.rows ?? rows)}
      emptyMessage={
        <EmptyState title={t('activity.list.empty.title')} description={emptyDesc} icon="pi pi-calendar" />
      }
    >
      <Column field="id" header={t('activity.list.columns.requestNo')} bodyClassName="tk-cell-strong" />
      <Column field="name" header={t('activity.list.columns.name')} bodyClassName="tk-cell-strong" />
      <Column header={t('activity.list.columns.placeDate')} body={placeDateBody} />
      <Column header={t('activity.list.columns.eventType')} body={(r: ActivityListItem) => r.eventType ?? ''} />
      <Column header={t('activity.list.columns.invitationType')} body={(r: ActivityListItem) => r.invitationType ?? ''} />
      <Column header={t('activity.list.columns.score')} body={scoreBody} />
      <Column header={t('activity.list.columns.priority')} body={priorityBody} />
      {/* Katılım Durumu (GM kararı) element-düzeyi yetki (012 T051, FR-024): sütun yalnız VIEW grant'ıyla
          render edilir (Column DataTable'ın doğrudan çocuğu olmalı → inline hasGrant). Asıl koruma
          backend'de (VIEW yoksa status=null döner). */}
      {hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_VIEW) && (
        <Column header={t('activity.list.columns.status')} body={statusBody} />
      )}
      {/* İşlemler kolonu yalnız Katıl/Katılma içerir → UPDATE yetkisi yoksa kolon TÜMÜYLE gizli (başlık dahil;
          boş kolon bırakmayız). Yetki varken: işlem görmüş satırda hücre boş, işlenmemişte kebab. Asıl koruma
          backend'de (@PreAuthorize UPDATE → 403). */}
      {hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_UPDATE) && (
        <Column
          header={t('activity.list.columns.actions')}
          body={(row: ActivityListItem) =>
            row.isProcessed ? null : (
              <RowActions row={row} onParticipation={onParticipation} t={t} />
            )
          }
          style={{ width: '5rem', textAlign: 'center' }}
        />
      )}
    </DataTable>
  )
}
